import React, { useEffect, useState, useCallback } from 'react';
import { Table, Spinner, Alert, Form, Row, Col, Button, Modal, Badge } from 'react-bootstrap';
import useAuthHeaders from '../../hooks/useAuthHeaders';

const API_BASE = process.env.REACT_APP_API_URL || 'https://localhost:5001/api/v1';

const toLimitInputValue = (value) => (value === null || value === undefined ? '' : String(value));

const parseLimitInput = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const AdminFeatureConfig = () => {
  const { getAuthHeaders } = useAuthHeaders();

  // Plans
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState(null);

  // Plan x Feature matrix for the selected plan
  const [matrix, setMatrix] = useState([]);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixError, setMatrixError] = useState(null);
  const [rowState, setRowState] = useState({}); // featureTypeId -> { saving, feedback }

  // Feature types (catalog) management
  const [featureTypes, setFeatureTypes] = useState([]);
  const [featureTypesLoading, setFeatureTypesLoading] = useState(true);
  const [featureTypesError, setFeatureTypesError] = useState(null);

  const [showNewFeatureModal, setShowNewFeatureModal] = useState(false);
  const [newFeatureCode, setNewFeatureCode] = useState('');
  const [newFeatureDescription, setNewFeatureDescription] = useState('');
  const [creatingFeature, setCreatingFeature] = useState(false);
  const [createFeatureFeedback, setCreateFeatureFeedback] = useState(null);

  const fetchPlans = useCallback(async () => {
    setPlansLoading(true);
    setPlansError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/admin/billing/plans`, { headers });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = await response.json();
      setPlans(data);
      if (data.length > 0) {
        setSelectedPlanId((prev) => prev || String(data[0].id));
      }
    } catch (err) {
      setPlansError(err.message);
    } finally {
      setPlansLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchFeatureTypes = useCallback(async () => {
    setFeatureTypesLoading(true);
    setFeatureTypesError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/admin/billing/feature-types`, { headers });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = await response.json();
      setFeatureTypes(data);
    } catch (err) {
      setFeatureTypesError(err.message);
    } finally {
      setFeatureTypesLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchMatrix = useCallback(async (planId) => {
    if (!planId) return;
    setMatrixLoading(true);
    setMatrixError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/admin/billing/plans/${planId}/features`, { headers });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = await response.json();
      setMatrix(data.map((item) => ({
        ...item,
        dailyLimitInput: toLimitInputValue(item.dailyLimit),
        monthlyLimitInput: toLimitInputValue(item.monthlyLimit),
      })));
      setRowState({});
    } catch (err) {
      setMatrixError(err.message);
    } finally {
      setMatrixLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);
  useEffect(() => { fetchFeatureTypes(); }, [fetchFeatureTypes]);
  useEffect(() => { fetchMatrix(selectedPlanId); }, [selectedPlanId, fetchMatrix]);

  const updateMatrixRow = (featureTypeId, changes) => {
    setMatrix((prev) => prev.map((row) =>
      row.featureTypeId === featureTypeId ? { ...row, ...changes } : row
    ));
  };

  const handleSaveRow = async (row) => {
    setRowState((prev) => ({ ...prev, [row.featureTypeId]: { saving: true, feedback: null } }));
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE}/admin/billing/plans/${selectedPlanId}/features/${row.featureTypeId}`,
        {
          method: 'PUT',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            isEnabled: row.isEnabled,
            dailyLimit: parseLimitInput(row.dailyLimitInput),
            monthlyLimit: parseLimitInput(row.monthlyLimitInput),
          }),
        }
      );
      const data = await response.json().catch(() => null);
      setRowState((prev) => ({
        ...prev,
        [row.featureTypeId]: {
          saving: false,
          feedback: { ok: response.ok, message: response.ok ? 'Guardado.' : (data?.message || `Error ${response.status}`) },
        },
      }));
    } catch (err) {
      setRowState((prev) => ({
        ...prev,
        [row.featureTypeId]: { saving: false, feedback: { ok: false, message: err.message } },
      }));
    }
  };

  const handleCreateFeature = async () => {
    setCreatingFeature(true);
    setCreateFeatureFeedback(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE}/admin/billing/feature-types`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: newFeatureCode.trim(), description: newFeatureDescription.trim() || null }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        setCreateFeatureFeedback({ ok: false, message: data?.message || `Error ${response.status}` });
        return;
      }
      setCreateFeatureFeedback({ ok: true, message: 'Funcionalidad creada.' });
      setNewFeatureCode('');
      setNewFeatureDescription('');
      await fetchFeatureTypes();
      await fetchMatrix(selectedPlanId);
    } catch (err) {
      setCreateFeatureFeedback({ ok: false, message: err.message });
    } finally {
      setCreatingFeature(false);
    }
  };

  const handleToggleFeatureActive = async (featureType) => {
    try {
      const headers = await getAuthHeaders();
      await fetch(`${API_BASE}/admin/billing/feature-types/${featureType.id}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: featureType.description, isActive: !featureType.isActive }),
      });
      await fetchFeatureTypes();
      await fetchMatrix(selectedPlanId);
    } catch {
      // silently ignore; list will just not reflect the change
    }
  };

  return (
    <div className="status-container">
      <div className="page-header mb-4">
        <div>
          <h2 className="page-title">Admin — Features y Límites por Plan</h2>
          <p className="page-subtitle text-muted">
            Habilita o deshabilita funcionalidades por plan y ajusta sus límites diarios/mensuales.
            Para dar de alta un Plan nuevo (con su Price en Stripe) sigue siendo manual.
          </p>
        </div>
      </div>

      {plansError && <Alert variant="danger">Error al cargar planes: {plansError}</Alert>}

      <div className="mb-3 p-3 border rounded bg-light">
        <Row className="g-2 align-items-end">
          <Col xs={12} md={4}>
            <Form.Label className="small mb-1">Plan</Form.Label>
            <Form.Select
              size="sm"
              value={selectedPlanId}
              onChange={(e) => setSelectedPlanId(e.target.value)}
              disabled={plansLoading || plans.length === 0}
            >
              {plans.map((p) => (
                <option key={p.id} value={p.id}>{p.name} ({p.code}){!p.active ? ' — inactivo' : ''}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>
      </div>

      {matrixError && <Alert variant="danger">Error al cargar features: {matrixError}</Alert>}

      {matrixLoading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" style={{ color: '#13a0ff' }} />
        </div>
      ) : (
        <div className="table-responsive mb-4">
          <Table hover size="sm" className="mb-2">
            <thead className="table-light">
              <tr>
                <th>Código</th>
                <th>Descripción</th>
                <th>Habilitado</th>
                <th>Límite diario</th>
                <th>Límite mensual</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {matrix.map((row) => {
                const state = rowState[row.featureTypeId] || {};
                return (
                  <tr key={row.featureTypeId}>
                    <td className="text-monospace">{row.featureCode}</td>
                    <td className="text-muted">{row.featureDescription || '—'}</td>
                    <td>
                      <Form.Check
                        type="switch"
                        checked={row.isEnabled}
                        onChange={(e) => updateMatrixRow(row.featureTypeId, { isEnabled: e.target.checked })}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        min="0"
                        placeholder="Ilimitado"
                        style={{ width: 110 }}
                        value={row.dailyLimitInput}
                        onChange={(e) => updateMatrixRow(row.featureTypeId, { dailyLimitInput: e.target.value })}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        min="0"
                        placeholder="Ilimitado"
                        style={{ width: 110 }}
                        value={row.monthlyLimitInput}
                        onChange={(e) => updateMatrixRow(row.featureTypeId, { monthlyLimitInput: e.target.value })}
                      />
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleSaveRow(row)}
                          disabled={state.saving}
                        >
                          {state.saving ? <Spinner animation="border" size="sm" /> : 'Guardar'}
                        </Button>
                        {state.feedback && (
                          <small className={state.feedback.ok ? 'text-success' : 'text-danger'}>
                            {state.feedback.message}
                          </small>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {matrix.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    No hay funcionalidades activas registradas.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      )}

      <div className="page-header mb-3 mt-5">
        <div>
          <h4 className="page-title">Funcionalidades (FeatureTypes)</h4>
        </div>
        <Button size="sm" variant="primary" onClick={() => { setShowNewFeatureModal(true); setCreateFeatureFeedback(null); }}>
          + Nueva funcionalidad
        </Button>
      </div>

      {featureTypesError && <Alert variant="danger">Error al cargar funcionalidades: {featureTypesError}</Alert>}

      {featureTypesLoading ? (
        <div className="d-flex justify-content-center py-3">
          <Spinner animation="border" size="sm" />
        </div>
      ) : (
        <div className="table-responsive">
          <Table hover size="sm">
            <thead className="table-light">
              <tr>
                <th>Código</th>
                <th>Descripción</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {featureTypes.map((ft) => (
                <tr key={ft.id}>
                  <td className="text-monospace">{ft.code}</td>
                  <td className="text-muted">{ft.description || '—'}</td>
                  <td>
                    <Badge bg={ft.isActive ? 'success' : 'secondary'}>
                      {ft.isActive ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant={ft.isActive ? 'outline-secondary' : 'outline-success'}
                      onClick={() => handleToggleFeatureActive(ft)}
                    >
                      {ft.isActive ? 'Desactivar' : 'Activar'}
                    </Button>
                  </td>
                </tr>
              ))}
              {featureTypes.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-muted py-4">
                    No hay funcionalidades registradas todavía.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      )}

      <Modal show={showNewFeatureModal} onHide={() => !creatingFeature && setShowNewFeatureModal(false)} centered>
        <Modal.Header closeButton={!creatingFeature}>
          <Modal.Title style={{ fontSize: '1rem' }}>Nueva funcionalidad</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="small">Código</Form.Label>
            <Form.Control
              size="sm"
              placeholder="ej. facturin"
              value={newFeatureCode}
              onChange={(e) => setNewFeatureCode(e.target.value)}
              disabled={creatingFeature}
            />
            <Form.Text className="text-muted">
              Solo letras, números y guion bajo. No se puede cambiar después de creado — es el valor que
              usan los endpoints con <code>[RequireFeature]</code>.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="small">Descripción</Form.Label>
            <Form.Control
              size="sm"
              as="textarea"
              rows={2}
              value={newFeatureDescription}
              onChange={(e) => setNewFeatureDescription(e.target.value)}
              disabled={creatingFeature}
            />
          </Form.Group>
          {createFeatureFeedback && (
            <Alert variant={createFeatureFeedback.ok ? 'success' : 'danger'} className="mb-0 small py-2">
              {createFeatureFeedback.message}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowNewFeatureModal(false)} disabled={creatingFeature}>
            Cerrar
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleCreateFeature}
            disabled={creatingFeature || !newFeatureCode.trim()}
          >
            {creatingFeature ? <Spinner animation="border" size="sm" /> : 'Crear'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminFeatureConfig;
